import React, {
  createContext,
  FormEventHandler,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';

import { makeStyles } from '@material-ui/core';
import { FormProvider, UseFormReturn } from 'react-hook-form';

import { Paper, Box } from '@mui/material';

import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';

import EditFooter from './components/EditFooter';
import { InnerViewNavigatorProps } from './components/InnerViewNavigator';
import ToolbarContent from './components/ToolbarContent';
import usePreviousLocation from './hooks/usePreviousLocation';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    padding: '30px'
  }
}));

interface EditPageContext {
  isEditable: boolean;
}

export const EditPageContext = createContext<EditPageContext>({
  isEditable: false
});

export const useEditPageContext = () => useContext(EditPageContext);

interface Props extends InnerViewNavigatorProps {
  methods: UseFormReturn<any>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  toggleInnerLayout: () => void;
  onCancel?: (args?: any) => void;
  isEditable?: any;
  withTabs?: boolean;
  onSetEditMode?: (isInEditMode: boolean) => void;
  onIsDirty?: (isDirty: boolean) => void;
}

const EditPage: React.FC<PropsWithChildren<Props>> = ({
  children,
  withTabs = false,
  methods,
  onSubmit,
  toggleInnerLayout,
  onCancel,
  setCurrentView,
  views,
  currentView,
  isEditable,
  navigatorValueGetter,
  navigatorLabelGetter,
  onSetEditMode,
  onIsDirty
}) => {
  const [isInEditMode, setIsInEditMode] = useState(false);
  const classes = useStyles();

  const handleSetEditMode = (editMode: boolean) => {
    onSetEditMode && onSetEditMode(editMode);
    setIsInEditMode(editMode);
  };

  const handleCancel = () => {
    onCancel && onCancel();
    handleSetEditMode(false);
  };
  const { formState } = methods;

  useEffect(() => {
    onIsDirty && onIsDirty(formState.isDirty);
  }, [formState.isDirty]);

  useEffect(() => {
    isEditable && handleSetEditMode(!isInEditMode);
  }, [isEditable]);

  usePreviousLocation(toggleInnerLayout);

  return (
    <Box mb={'97px'} pt={'40px'}>
      {formState.isDirty && !withTabs ? (
        <Box
          sx={{
            display: 'flex',
            marginBottom: 2,
            flexDirection: 'row-reverse'
          }}
        >
          <UnsavedDataAlert />
        </Box>
      ) : (
        ''
      )}
      <EditPageContext.Provider
        value={{
          isEditable: isInEditMode
        }}
      >
        <FormProvider {...methods}>
          <ToolbarContent
            setIsInEditMode={handleSetEditMode}
            isInEditMode={isInEditMode}
            toggleInnerLayout={toggleInnerLayout}
            setCurrentView={setCurrentView}
            views={views}
            currentView={currentView}
            navigatorValueGetter={navigatorValueGetter}
            navigatorLabelGetter={navigatorLabelGetter}
          />
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <Paper
              elevation={0}
              variant="elevation"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '30px'
              }}
              classes={classes.root}
            >
              <>
                {children}
                {isInEditMode && (
                  <EditFooter
                    onCancelClick={handleCancel}
                    submitDisabled={!formState.isDirty}
                  />
                )}
              </>
            </Paper>
          </form>
        </FormProvider>
      </EditPageContext.Provider>
    </Box>
  );
};

export default EditPage;
